import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import cn from 'classnames';
import styles from './ConfirmStonePurchaseModal.module.css';
import { useTranslation } from '@/hooks/useTranslation';
import { CommentStoneOption } from '@/utils/commentStones';
import A_Button from '@/components/A_Atoms/A_Button/A_Button';

export interface ConfirmStonePurchaseModalProps {
  isOpen: boolean;
  stone: CommentStoneOption | null;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

const formatPriceRu = (price: number) => `${price.toLocaleString('ru-RU')} $`;

export default function ConfirmStonePurchaseModal({
  isOpen,
  stone,
  onConfirm,
  onCancel,
  isLoading = false,
}: ConfirmStonePurchaseModalProps) {
  const t = useTranslation();

  const priceLabel = useMemo(() => {
    if (!stone) return '';
    return stone.price > 0
      ? formatPriceRu(stone.price)
      : t('capsuleReactions.freeStone');
  }, [stone, t]);

  const confirmText = useMemo(() => {
    if (!stone) return '';
    const template = t('capsuleReactions.purchaseConfirmText');
    return template
      .replace('{{name}}', stone.name)
      .replace('{{price}}', priceLabel);
  }, [stone, t, priceLabel]);

  if (!isOpen || !stone) return null;

  return createPortal(
    <div className={styles.backdrop} onClick={onCancel}>
      <div className={styles.sheet} onClick={e => e.stopPropagation()}>
        <div className={styles.content}>
          <img src={stone.imageUrl} alt={stone.name} className={styles.image} />
          <div className={styles.meta}>
            <span className={cn(styles.name, 'text-p-6')}>{stone.name}</span>
            <span className={cn(styles.price, 'text-p-7')}>{priceLabel}</span>
          </div>
          <p className={cn(styles.text, 'text-p-7')}>{confirmText}</p>
        </div>
        <div className={styles.actions}>
          <A_Button
            variant='primary'
            size='small'
            fontSize='text-p-4'
            disabled={isLoading}
            onClick={() => void onConfirm()}
          >
            {t('capsuleReactions.purchaseConfirm')}
          </A_Button>
          <A_Button
            variant='secondary'
            size='small'
            fontSize='text-p-4'
            disabled={isLoading}
            onClick={onCancel}
          >
            {t('capsuleReactions.purchaseCancel')}
          </A_Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
